import { useNavigate } from "react-router-dom";
import {
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Rating,
} from "@mui/material";
import SearchBar from "../../components/SearchBar";
import { useState } from "react";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import AddRatingReply from "../../Modals/addReplyRating";
import Pagination from "../../components/Panigation";
import { useGetRatingsListQuery, useDeleteRatingMutation } from "../../store/api/manageAPI"; // Import mutation
import moment from "moment";
import DeleteConfirmationModal from "../../Modals/deleteConfirmation"; // Import DeleteConfirmationModal
import { toast } from "react-toastify"; // Import toast for notifications

const Ratings = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [rowsPerPage] = useState(10);

    const { data: ratingsData, isLoading, isFetching } = useGetRatingsListQuery({
        page: page,
        limit: rowsPerPage,
        search: searchTerm,
    });

    const [deleteRating, { isLoading: isDeleting }] = useDeleteRatingMutation(); // Mutation hook

    const rows = ratingsData?.data?.list || [];
    const totalCount = ratingsData?.data?.total || 0;

    const columns = [
        { id: "srNo", label: "Sr. No." },
        { id: "userDetails", label: "User Details" },
        { id: "orderId", label: "Order ID" },
        { id: "rating", label: "Rating" },
        { id: "review", label: "Review" },
        { id: "date", label: "Date" },
        { id: "action", label: "Action" },
    ];
    const [openModal, setOpenModal] = useState(false);
    const [selectedRating, setSelectedRating] = useState<any>(null);

    // Delete Modal State
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handleDeleteClick = (id: string) => {
        setSelectedId(id);
        setOpenDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedId) {
            try {
                const res = await deleteRating({ id: selectedId }).unwrap();
                if (res.success) {
                    toast.success(res.message || "Rating deleted successfully");
                    setOpenDeleteModal(false);
                    setSelectedId(null);
                } else {
                    toast.error(res.message || "Failed to delete rating");
                }
            } catch (error: any) {
                toast.error(error?.data?.message || "An error occurred while deleting");
            }
        }
    };

    return (
        <>
            <div className="main_title">
                <h1>Manage Ratings & Reviews</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> - Ratings & Reviews
                </p>
            </div>

            <div className="cards">
                {/* Table Header */}
                <div className="table_header">
                    <div className="left_s">
                        <Box className="search_box">
                            <SearchBar
                                searchTerm={searchTerm}
                                setDebouncedSearchTerm={(term) => {
                                    setSearchTerm(term);
                                    setPage(1); // Reset to first page on search
                                }}
                                placeholder="Search by user, product, or order..."
                            />
                        </Box>
                    </div>

                </div>

                {/* Table */}
                <TableContainer className="table_container">
                    <Table>
                        <TableHead>
                            <TableRow hover>
                                {columns.map((column) => (
                                    <TableCell key={column.id}>{column.label}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {isLoading || isFetching ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" style={{ padding: "50px" }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : rows.length > 0 ? (
                                rows.map((row: any, index: number) => (
                                    <TableRow hover key={row._id}>
                                        <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>

                                        {/* User Details */}
                                        <TableCell>
                                            <a
                                                href={`/manage-users/details/${row.userId}`} // Assuming dynamic link if needed
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    // navigate(`/manage-users/details/${row.userId}`); // Uncomment if user details page exists
                                                }}
                                                className="user_block click_action"
                                                style={{ pointerEvents: "none", cursor: "default" }} // Disable link for now as per minimal change
                                            >
                                                <figure>
                                                    <img
                                                        src={row.user?.image || "/static/images/user_placeholder.png"}
                                                        alt="User"
                                                        onError={(e) => {
                                                            (e.target as HTMLImageElement).src = "/static/images/user_placeholder.png";
                                                        }}
                                                    />
                                                </figure>
                                                <p>
                                                    <b>{row.user?.fullName || "N/A"}</b>
                                                    {row.user?.email || "N/A"}
                                                    <br />
                                                    {row.user?.countryCode} {row.user?.phone}
                                                </p>
                                            </a>
                                        </TableCell>

                                        <TableCell>#{row.orderId?.substring(row.orderId.length - 6).toUpperCase()}</TableCell>

                                        {/* Rating */}
                                        <TableCell>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Rating name="read-only" value={row.score} precision={0.1} readOnly size="small" />
                                            </div>
                                        </TableCell>

                                        <TableCell className="max_c2">{row.reviews}</TableCell>
                                        <TableCell>{moment(row.createdAt).format("DD MMM YYYY")}</TableCell>

                                        {/* Actions */}
                                        <TableCell>
                                            <div className="table_actions v2">
                                                <Box>
                                                    <IconButton onClick={() => {
                                                        setSelectedRating(row);
                                                        setOpenModal(true);
                                                    }}>
                                                        <RemoveRedEyeIcon />
                                                    </IconButton>
                                                </Box>
                                                <Box>
                                                    <IconButton onClick={() => handleDeleteClick(row._id)}>
                                                        <img
                                                            src="/static/images/trash2_icon.svg"
                                                            alt="Delete"
                                                        />
                                                    </IconButton>
                                                </Box>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        No records found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {!isLoading && totalCount > 0 && (
                    <Box display="flex" justifyContent="flex-end" mt={2}>
                        <Pagination
                            module={null}
                            page={page}
                            setPage={setPage as any} // Casting to satisfy Dispatch<SetStateAction<number>> if setPage type inference is strict
                            onPageChange={handlePageChange}
                            totalPages={Math.ceil(totalCount / rowsPerPage)}
                            totalCount={totalCount}
                            limit={rowsPerPage}
                        />
                    </Box>
                )}
            </div>
            <AddRatingReply
                open={openModal}
                onClose={handleCloseModal}
                setOpen={setOpenModal}
                data={selectedRating}
            />
            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                open={openDeleteModal}
                onClose={() => setOpenDeleteModal(false)}
                setOpen={setOpenDeleteModal}
                onConfirm={handleConfirmDelete}
                itemName="this rating"
                isLoading={isDeleting}
            />
        </>
    );
};

export default Ratings;
